import { MantineColor } from '@mantine/core';
import i18n from 'i18n';
import { Evaluation, EvaluationTest, EvaluationTestStatus } from 'shared/interfaces/Evaluation';
import { getReadableFormatDateTime } from 'shared/utils/date';

export const MAX_SCENARIOS = 10;
export const MIN_SCENARIOS = 1;
export const MAX_SCENARIO_LENGTH = 500;

export const MIN_TESTS_COUNT = 5;
export const MAX_TESTS_COUNT = 30;
export const INITIAL_TESTS_COUNT = 10;

export const MAX_EVALUATION_NAME_LENGTH = 100;
export const MAX_SINGLE_STEP_QUESTION_LENGTH = 500;
export const MAX_SINGLE_STEP_QUESTIONS = 30;
export const MIN_SINGLE_STEP_QUESTIONS = 1;

export const EVALUATION_REFETCH_INTERVAL = 3000;
export const EVALUATION_EXPORT_TIMEOUT_MS = 1_000_000;

export interface EvaluationTestInfo {
  onRunEvaluationTest: () => void;
  evaluationTest: EvaluationTest;
}

export interface EvaluationStatusInfo {
  status: 'pending' | 'running' | 'completed' | 'failed';
  badgeColor: MantineColor;
  badgeText: string;
}

export const getEvaluationStatusInfo = (evaluation: Evaluation): EvaluationStatusInfo => {
  const hasFailedTests = evaluation.evaluationTests.some((test) => test.status === EvaluationTestStatus.FAILED);
  const hasRunningTests = evaluation.evaluationTests.some((test) => test.status === EvaluationTestStatus.RUNNING);
  const hasPendingTests = evaluation.evaluationTests.some((test) => test.status === EvaluationTestStatus.PENDING);

  if (hasFailedTests) {
    return {
      status: 'failed',
      badgeColor: 'red',
      badgeText: i18n.t('APP.STATUS.FAILED')
    };
  } else if (hasRunningTests) {
    return {
      status: 'running',
      badgeColor: 'sky',
      badgeText: i18n.t('APP.STATUS.RUNNING')
    };
  } else if (hasPendingTests) {
    return {
      status: 'pending',
      badgeColor: 'amber',
      badgeText: i18n.t('APP.STATUS.PENDING')
    };
  } else {
    return {
      status: 'completed',
      badgeColor: 'green',
      badgeText: i18n.t('APP.STATUS.COMPLETED')
    };
  }
};

export const getEvaluationTestReadableTimestamp = (evaluationTest: EvaluationTest) => {
  if (evaluationTest.status === EvaluationTestStatus.RUNNING) {
    return i18n.t('APP.STATUS.RUNNING');
  }

  // Check if the test has been run
  if (evaluationTest.result && evaluationTest.result.lastRanAt) {
    // Return the actual timestamp when the test was last run
    return getReadableFormatDateTime(new Date(evaluationTest.result.lastRanAt).getTime());
  }

  // Return a message indicating the test hasn't been run yet
  return i18n.t('APP.STATUS.NOT_RUN_YET');
};

export const anyEvaluationHasRunningTests = (evaluations: Evaluation[]): boolean => {
  return evaluations.some((evaluation) =>
    evaluation.evaluationTests.some((test) => test.status === EvaluationTestStatus.RUNNING)
  );
};
